import {
  AppointmentCreateDTO,
  AppointmentWindowListDTO,
  CompanyWindowListDTO,
  Company,
  AppointmentStatusEnum,
  BlockTypeEnum,
  TimezoneAbbreviationEnum
} from '../api/apiTypes.shim';

import { DateUtils } from '../utils/date.utils';

export function getEarliestAppointmentDate(
  appointmentWindow: Pick<AppointmentWindowListDTO, 'startTime'>,
  date: Date,
  timezone: TimezoneAbbreviationEnum
): Date {
  const windowStart = DateUtils.mergeDateAndTime(date, appointmentWindow.startTime, timezone);
  const now = new Date();
  if (DateUtils.isSame(now, date, 'day', { timezone }) && DateUtils.isAfter(now, windowStart)) {
    return DateUtils.roundToNextMinuteInterval(now, 30);
  } else {
    return windowStart;
  }
}

export function getLatestAppointmentDateInfo(
  appointmentWindow: Pick<AppointmentWindowListDTO, 'endTime'>,
  date: Date,
  timezone: TimezoneAbbreviationEnum
) {
  const windowEnd = DateUtils.mergeDateAndTime(date, appointmentWindow.endTime, timezone);
  const windowEndPlus30 = DateUtils.add(windowEnd, { minutes: 30 });
  const elevenThirtyPM = DateUtils.setUnits(date, { hour: 23, minute: 30, second: 0, millisecond: 0 }, { timezone });
  const showMidnightArrival = DateUtils.isAfter(windowEnd, elevenThirtyPM);
  if (DateUtils.isAfter(windowEndPlus30, elevenThirtyPM)) {
    return { latestSelectableDate: elevenThirtyPM, showMidnightArrival };
  } else {
    return { latestSelectableDate: windowEndPlus30, showMidnightArrival };
  }
}

/**
 * @param date - day to initialize `appointment.date` (note: the `appointment.date` will be initialized to
 *                      the startTime OR the earliest minute after the startTime if now is after the startTime)
 * @param company - company to initialize an Appointment for
 * @param appointmentWindow - AppointmentWindow to initialize an Appointment for
 * @param companyWindow - CompanyWindow to initialize an Appointment for
 * @returns Appointment DTO to be used with to create an appointment with status of AgentHold
 */
export const initAgentHoldAppointment = (
  date: Date,
  company: Pick<Company, 'id' | 'timezone'>,
  appointmentWindow: Pick<AppointmentWindowListDTO, 'id' | 'blockType' | 'startTime'>,
  companyWindow: Pick<CompanyWindowListDTO, 'id' | 'trades'>
): AppointmentCreateDTO => {
  // When initializing the date for an appointment, if it's ALL DAY, it should start @ 8AM, unless the appointment
  // is being booked on today and it's after 8 AM
  let appointmentDate;
  if (appointmentWindow.blockType === BlockTypeEnum.ALL_DAY) {
    const eightAmDate = DateUtils.setUnits(
      date,
      { hour: 8, minute: 0, second: 0, millisecond: 0 },
      { timezone: company.timezone }
    );
    const now = new Date();

    if (DateUtils.isSame(now, date, 'day', { timezone: company.timezone }) && now > eightAmDate) {
      const appointmentDateWithNowTime = DateUtils.mergeDateAndTime(date, now, company.timezone);
      appointmentDate = DateUtils.roundToNextMinuteInterval(appointmentDateWithNowTime, 30);
    } else {
      appointmentDate = eightAmDate;
    }
  } else {
    appointmentDate = getEarliestAppointmentDate(appointmentWindow, date, company.timezone);
  }

  return {
    appointmentWindowId: appointmentWindow.id,
    date: appointmentDate,
    status: AppointmentStatusEnum.AgentHold,
    trade: companyWindow.trades[0]
  };
};

export const isAppointmentAfterHours = (args: {
  currentTime?: Date;
  appointmentStart: string;
  timezone: TimezoneAbbreviationEnum;
}) => {
  const { currentTime, appointmentStart, timezone } = args;

  const now = currentTime || new Date();

  const dayOfWeek = DateUtils.getDayOfWeek(now, { timezone });
  const currentHour = DateUtils.getHour(now, { timezone });

  // Is today sat/sunday OR friday after 4pm?
  if ([0, 6].includes(dayOfWeek) || (dayOfWeek === 5 && currentHour >= 16)) {
    // add '4' days to bump to next week regardless of fri/sat/sunday
    const fourDaysInFuture = DateUtils.add(now, { days: 4 });
    const nextMonday = DateUtils.toStartOf(fourDaysInFuture, 'week', { timezone });
    const nextMonday9am = DateUtils.setUnits(
      nextMonday,
      { hour: 9, minute: 0, second: 0, millisecond: 0 },
      { timezone }
    );

    // Is the appointment.start before 9am next Monday?
    if (DateUtils.isBefore(appointmentStart, nextMonday9am)) {
      return true;
    }
    // Is it currently before 9am on a weekday?
  } else if (currentHour < 9) {
    const today9am = DateUtils.setUnits(now, { hour: 9, minute: 0, second: 0, millisecond: 0 }, { timezone });

    // Is the appointment.start before TODAY at 9am?
    if (DateUtils.isBefore(appointmentStart, today9am)) {
      return true;
    }
    // Is it currently after 4pm on a non-friday weekday?
  } else if (currentHour >= 16) {
    const tomorrow = DateUtils.add(now, { days: 1 });
    const tomorrow9am = DateUtils.setUnits(tomorrow, { hour: 9, minute: 0, second: 0, millisecond: 0 }, { timezone });
    // Is the appointment.start before tomorrow at 9am?
    if (DateUtils.isBefore(appointmentStart, tomorrow9am)) {
      return true;
    }
  }
  return false;
};
